import { useEffect, useState, useCallback } from 'react';
import api, { API } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, FileSpreadsheet, Loader2, TriangleAlert } from 'lucide-react';
import { SITE_STATUS, POSITIONS, ACTION_LABELS } from '../lib/status';
import { toast } from 'sonner';

const TYPES = [
  { v: 'all', l: 'Site Status' }, { v: 'installation', l: 'Installation' },
  { v: 'inspection', l: 'Inspection' }, { v: 'damage', l: 'Damage' }, { v: 'repair', l: 'Repair' },
];

export default function Reports() {
  const [societies, setSocieties] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [societyId, setSocietyId] = useState('all');
  const [campaignId, setCampaignId] = useState('all');
  const [type, setType] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  useEffect(() => { api.get('/societies').then((r) => setSocieties(r.data)); api.get('/campaigns').then((r) => setCampaigns(r.data)); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ type });
    if (societyId !== 'all') p.set('society_id', societyId);
    if (campaignId !== 'all') p.set('campaign_id', campaignId);
    api.get(`/reports/summary?${p.toString()}`).then((r) => { setReport(r.data); setLoading(false); });
  }, [societyId, campaignId, type]);
  useEffect(() => { load(); }, [load]);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const p = new URLSearchParams({ type, fmt });
      if (societyId !== 'all') p.set('society_id', societyId);
      if (campaignId !== 'all') p.set('campaign_id', campaignId);
      const res = await api.get(`/reports/export?${p.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${type}_report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
    setExporting('');
  };

  const t = report?.totals || {};

  return (
    <div className="space-y-5">
      <div><p className="overline text-primary">Analytics</p><h1 className="font-head text-3xl font-bold">Reports</h1></div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={societyId} onValueChange={setSocietyId}>
          <SelectTrigger data-testid="report-society" className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All societies</SelectItem>{societies.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={campaignId} onValueChange={setCampaignId}>
          <SelectTrigger data-testid="report-campaign" className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All campaigns</SelectItem>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="report-type" className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" data-testid="export-pdf-report-btn" onClick={() => doExport('pdf')} disabled={!!exporting}>{exporting === 'pdf' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileText className="mr-1 h-4 w-4" />} PDF</Button>
          <Button variant="secondary" data-testid="export-excel-report-btn" onClick={() => doExport('excel')} disabled={!!exporting}>{exporting === 'excel' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />} Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total Sites" value={t.total || 0} />
        <Stat label="Installed" value={t.installed || 0} tone="text-blue-400" />
        <Stat label="Damaged / Repair" value={t.damaged || 0} tone="text-rose-400" />
        <Stat label="Damage %" value={`${t.damage_pct || 0}%`} tone={t.damage_pct > 15 ? 'text-rose-400' : 'text-emerald-400'} icon={t.damage_pct > 15 ? TriangleAlert : null} />
      </div>

      {report?.status_counts && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(report.status_counts).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs">
              <span className="h-2 w-2 rounded-full" style={{ background: (SITE_STATUS[k] || {}).dot }} /> {(SITE_STATUS[k] || {}).label}: <b>{v}</b>
            </span>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-2">
        {loading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Site</TableHead><TableHead>Society</TableHead><TableHead>Position</TableHead><TableHead>Status</TableHead><TableHead>Campaign</TableHead><TableHead>Last Action</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
              <TableBody>
                {report?.rows?.map((r, i) => (
                  <TableRow key={i} data-testid={`report-row-${i}`}>
                    <TableCell className="font-mono text-[11px]">{r.site_code}</TableCell>
                    <TableCell className="text-xs">{r.society}<br /><span className="text-muted-foreground">{r.tower} · {r.lift}</span></TableCell>
                    <TableCell className="text-xs">{r.position}</TableCell>
                    <TableCell><span className="inline-flex items-center gap-1.5 text-xs"><span className="h-2 w-2 rounded-full" style={{ background: (SITE_STATUS[r.status] || {}).dot }} />{(SITE_STATUS[r.status] || {}).label}</span></TableCell>
                    <TableCell className="text-xs">{r.campaign}</TableCell>
                    <TableCell className="text-xs">{ACTION_LABELS[r.last_action] || r.last_action}</TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground">{(r.timestamp || '').slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
                {!report?.rows?.length && <TableRow><TableCell colSpan={7} className="py-6 text-center text-muted-foreground">No data for these filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = 'text-foreground', icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="overline text-muted-foreground">{label}</p>
      <p className={`mt-1 flex items-center gap-2 font-head text-3xl font-extrabold ${tone}`}>{Icon && <Icon className="h-5 w-5" />}{value}</p>
    </div>
  );
}
